import { Box, Button, Card, CardMedia, Stack, styled, Typography } from '@mui/material'
import { sanitize } from 'dompurify'
import type { FC } from 'react'
import { useMemo } from 'react'

import { isNotNull } from '@hcr/utils'

import { COLORS } from '../../../../constants'
import type { Link } from '../../../../models'
import { optimizelyCss } from '../../../../styles'
import { isLibPropValid, unit } from '../../../../utils'
import { UnstyledLink } from '../../../navigation'
import { CardVariant, CardEmbedment } from '../types'

export interface CardPrimaryProps {
  description: string
  embedment: CardEmbedment
  imageUrl: string | null
  link: Link | null
  title: string | null
  variant: CardVariant
}

const LINE_HEIGHT = 6
const CARD_MAX_HEIGHT = 97.5
const IMAGE_HEIGHT = 40
const BUTTON_HEIGHT = 14
const TITLE_HEIGHT = 6
const CONTENT_PADDINGS = 10
const GAP = 2.5

export const CardPrimary: FC<CardPrimaryProps> = ({ description, embedment, imageUrl, link, title, variant }) => {
  // Refactoring opportunity: Simplify the logic, try using pure CSS or extract to a function that could be unit tested
  const lineClamp = useMemo(() => {
    if (isNotNull(imageUrl) && isNotNull(title) && isNotNull(link)) {
      return (CARD_MAX_HEIGHT - IMAGE_HEIGHT - BUTTON_HEIGHT - TITLE_HEIGHT - CONTENT_PADDINGS - 2 * GAP) / LINE_HEIGHT
    }

    if (isNotNull(imageUrl) && isNotNull(link)) {
      return (CARD_MAX_HEIGHT - IMAGE_HEIGHT - BUTTON_HEIGHT - CONTENT_PADDINGS - GAP) / LINE_HEIGHT
    }

    if (isNotNull(imageUrl) && isNotNull(title)) {
      return (CARD_MAX_HEIGHT - IMAGE_HEIGHT - TITLE_HEIGHT - CONTENT_PADDINGS - GAP) / LINE_HEIGHT
    }

    if (isNotNull(imageUrl)) {
      return (CARD_MAX_HEIGHT - IMAGE_HEIGHT - CONTENT_PADDINGS) / LINE_HEIGHT
    }

    if (isNotNull(title) && isNotNull(link)) {
      return (CARD_MAX_HEIGHT - BUTTON_HEIGHT - TITLE_HEIGHT - CONTENT_PADDINGS - 2 * GAP) / LINE_HEIGHT
    }

    if (isNotNull(title)) {
      return (CARD_MAX_HEIGHT - TITLE_HEIGHT - CONTENT_PADDINGS - GAP) / LINE_HEIGHT
    }

    if (isNotNull(link)) {
      return (CARD_MAX_HEIGHT - BUTTON_HEIGHT - CONTENT_PADDINGS - GAP) / LINE_HEIGHT
    }

    return (CARD_MAX_HEIGHT - CONTENT_PADDINGS) / LINE_HEIGHT
  }, [imageUrl, link, title])

  return (
    <Container cardEmbedment={embedment} cardVariant={variant}>
      {isNotNull(imageUrl) && <CardMedia component='img' height={unit(IMAGE_HEIGHT)} image={imageUrl} />}
      <Content>
        {isNotNull(title) && <Title variant='titleM'>{title}</Title>}
        <Typography variant='bodyM'>
          <Description lineClamp={Math.round(lineClamp)} dangerouslySetInnerHTML={{ __html: sanitize(description) }} />
        </Typography>
        {isNotNull(link) && (
          <Button component={UnstyledLink} to={link.to} variant='outlined' color='black'>
            {link.label}
          </Button>
        )}
      </Content>
    </Container>
  )
}

const Container = styled(Card, { shouldForwardProp: isLibPropValid })<{
  readonly cardEmbedment: CardEmbedment
  readonly cardVariant: CardVariant
}>`
  background-color: ${props => (props.cardVariant === CardVariant.Regular ? COLORS.vaalea['60%'] : COLORS.white)};
  height: ${props => (props.cardEmbedment === CardEmbedment.Standalone ? 'auto' : unit(CARD_MAX_HEIGHT))};
  width: ${props => (props.cardEmbedment === CardEmbedment.Standalone ? '100%' : unit(70))};
`

const Content = styled(Stack)`
  gap: ${unit(GAP)};
  padding: ${unit(5)};
`

const Title = styled(Typography)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const Description = styled(Box, { shouldForwardProp: isLibPropValid })<{ readonly lineClamp: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.lineClamp};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: normal;

  ${optimizelyCss};
`
